import { BarChart, XAxis, YAxis, Tooltip, Bar, ReferenceLine, ResponsiveContainer } from "recharts";

import { SortOrder } from "../../service/utils/constants";
import { WasteTotalVerticalEntry } from "../../service/utils/interfaces";

interface LeaderboardGraphProps {
  arrayData: WasteTotalVerticalEntry[],
  darkMode: boolean,
  filterVal: number,
  sortOrder: SortOrder,
  width: number
};

const LeaderboardGraph = (props: LeaderboardGraphProps) => {
  const leftMargin = props.width >= 1920 ? 140 :
    props.width >= 1440 ? 80 :
      props.width >= 1280 ? 75 :
        props.width >= 1023 ? 60 : 60;
        
  const height = props.width >= 1920 ? 170 :
    props.width >= 1440 ? 185 :
      props.width >= 1280 ? 188 :
        props.width >= 1023 ? 194 : 194;
  
  // Show all items from the selected array (top or bottom)
  const displayedArray = props.arrayData;

  // FIXED Y-AXIS WIDTH - DO NOT CALCULATE BASED ON TEXT LENGTH
  const yAxisWidth = 120; // Fixed width to prevent movement

  const minValue = displayedArray.reduce((min, el) => {
    const value = props.filterVal === 1 ? el.value1 :
      props.filterVal === 2 ? el.value2 :
        el.value3;
    return Math.min(min, value);
  }, 0);
  const maxValue = displayedArray.reduce((max, el) => {
    const value = props.filterVal === 1 ? el.value1 :
      props.filterVal === 2 ? el.value2 :
        el.value3;
    return Math.max(max, value);
  }, 0);

  // Custom tick component for conditional multi-line text
  const CustomYAxisTick = (props: any) => {
    const { x, y, payload } = props;
    const text = payload.value;
    const fontSize = parseInt(getComputedStyle(document.documentElement).getPropertyValue('--paragraph-font').replace('px', '')) || 12;
    const lineHeight = fontSize * 1.2;
    
    // Calculate approximate text width
    const approximateTextWidth = text.length * (fontSize * 0.6);

    // Only apply multi-line breaking if text width exceeds 150px
    if (approximateTextWidth > 127) {
      // Use a fixed character limit for line breaking
      const maxCharsPerLine = 20; // Reduced to fit in fixed width
      
      // Split text into words and create lines
      const words = text.split(' ');
      const lines: string[] = [];
      let currentLine = '';
      
      words.forEach((word: string) => {
        const testLine = currentLine ? `${currentLine} ${word}` : word;
        if (testLine.length <= maxCharsPerLine) {
          currentLine = testLine;
        } else {
          if (currentLine) {
            lines.push(currentLine);
            currentLine = word;
          } else {
            // Single word is too long, truncate it
            lines.push(word.substring(0, maxCharsPerLine - 3) + '...');
            currentLine = '';
          }
        }
      });
      
      if (currentLine) {
        lines.push(currentLine);
      }
      
      // Limit to maximum 2 lines
      const maxLines = 2;
      const finalLines = lines.slice(0, maxLines);
      if (lines.length > maxLines) {
        finalLines[maxLines - 1] = finalLines[maxLines - 1].substring(0, maxCharsPerLine - 3) + '...';
      }
      
      // Calculate starting Y position to center the text vertically
      const totalHeight = finalLines.length * lineHeight;
      const startY = y - (totalHeight / 2) + (lineHeight / 2);
      
      return (
        <g>
          {finalLines.map((line, index) => (
            <text
              key={index}
              x={x - 5}
              y={startY + (index * lineHeight)}
              textAnchor="end"
              fill={props.darkMode ? '#fff' : '#666'}
              fontSize="var(--paragraph-font)"
              style={{ pointerEvents: 'none' }}
            >
              {line}
            </text>
          ))}
        </g>
      );
    } else {
      // Use single line for shorter text
      return (
        <text
          x={x - 5}
          y={y}
          textAnchor="end"
          fill={props.darkMode ? '#fff' : '#666'}
          fontSize="var(--paragraph-font)"
          style={{ pointerEvents: 'none' }}
        >
          {text}
        </text>
      );
    }
  };

  return (
    <ResponsiveContainer width="100%" height={height}>
    <BarChart
      layout='vertical'
      data={displayedArray}
      margin={{
        left: Math.max(0, leftMargin - (200 - yAxisWidth)),
        right: 10,
        top: 5,
        bottom: 5
      }}
      style={{cursor: 'pointer'}}
    >
      <XAxis
        type='number'
        tick={{fill: props.darkMode ? '#fff' : '#666', fontSize: 'var(--paragraph-font)'}}
        domain={[minValue, maxValue]}
      />
      <YAxis
        dataKey="name"
        type='category'
        tick={<CustomYAxisTick darkMode={props.darkMode} />}
        width={yAxisWidth}
      />
      <Tooltip
        cursor={false}
        wrapperClassName={'tooltip-wrapper' + (props.darkMode ? ' dark' : '')}
      />
      <ReferenceLine x={0} stroke={props.darkMode ? '#fff' : '#666'} />
      {props.filterVal === 1 && (
        <Bar
          name='% Recycling Rate'
          barSize={15}
          dataKey="value1"
          fill={props.darkMode ? '#00cccc' : '#00b2b2'}
        />
      )}
      {props.filterVal === 2 && (
        <Bar
          name='% Emissions Rate'
          barSize={15}
          dataKey="value2"
          fill={props.darkMode ? '#7c97de' : '#122048'}
        />
      )}
      {props.filterVal === 3 && (
        <Bar
          name='% Waste Reduced'
          barSize={15}
          dataKey="value3"
          fill="#ff3600"
        />
      )}
    </BarChart>
    </ResponsiveContainer>
  );
};

export default LeaderboardGraph;
